<template>
  <v-container class="demo-page">
    <!-- Zgjedhja e gjuhes ruhet ne localStorage me celesin 'locale', te njejtin
         qe lexon aplikacioni pas hyrjes — ndaj kush hyn ketu ne anglisht, e
         gjen anglishten edhe brenda. -->
    <div class="d-flex justify-end mb-2">
      <div class="lang-switch">
        <button
          type="button"
          class="lang-opt"
          :class="{ 'lang-opt--active': lang === 'sq' }"
          @click="setLang('sq')"
        >
          SQ
        </button>
        <button
          type="button"
          class="lang-opt"
          :class="{ 'lang-opt--active': lang === 'en' }"
          @click="setLang('en')"
        >
          EN
        </button>
      </div>
    </div>

    <div class="text-center mb-8">
      <v-icon size="46" color="primary">mdi-bus-marker</v-icon>
      <h1 class="text-h5 font-weight-bold mt-3 mb-1">{{ txt.title }}</h1>
      <p class="grey--text mb-0">
        {{ txt.subtitle }}
      </p>
    </div>

    <v-row justify="center">
      <v-col v-for="view in views" :key="view.key" cols="12" sm="6" md="4">
        <v-card
          outlined
          hover
          class="demo-card"
          :disabled="loading !== null"
          @click="enter(view.key)"
        >
          <v-card-text class="text-center py-8">
            <v-avatar :color="view.color" size="56" class="mb-4">
              <v-icon color="white" size="28">{{ view.icon }}</v-icon>
            </v-avatar>

            <h3 class="text-subtitle-1 font-weight-bold mb-2">{{ view.title[lang] }}</h3>
            <p class="text-body-2 grey--text mb-4">{{ view.description[lang] }}</p>

            <v-btn
              :color="view.color"
              depressed
              block
              :loading="loading === view.key"
            >
              {{ txt.enter }}
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" dense text class="mt-6">
      {{ error }}
    </v-alert>

    <div class="text-center mt-8">
      <v-alert dense text type="info" class="d-inline-block text-left">
        {{ txt.notice }}
      </v-alert>
      <div class="mt-4">
        <router-link to="/login" class="text-body-2">{{ txt.haveAccount }}</router-link>
      </div>
    </div>
  </v-container>
</template>

<script>
import AuthService from '@/services/AuthService';
import { setDocumentLang } from '@/utils/document';
import { loadLocaleMessagesAsync } from '@/i18n';

/** Tekstet e kesaj faqeje ne te dy gjuhet. */
const TEKSTET = {
  sq: {
    title: 'BusTracker — Versioni Demo',
    subtitle: 'Zgjidhni si dëshironi ta shihni sistemin. Hyrja bëhet pa fjalëkalim.',
    enter: 'Hyr',
    notice: 'Mund të provoni gjithçka — çdo ndryshim pastrohet automatikisht çdo natë.',
    haveAccount: 'Keni llogari? Kyçuni këtu',
    failed: 'Hyrja dështoi.',
    unavailable: 'Versioni demo nuk është i disponueshëm për momentin.',
  },
  en: {
    title: 'BusTracker — Demo Version',
    subtitle: 'Choose how you want to see the system. No password needed.',
    enter: 'Enter',
    notice: 'Feel free to try everything — all changes are cleared automatically each night.',
    haveAccount: 'Already have an account? Sign in here',
    failed: 'Sign-in failed.',
    unavailable: 'The demo is not available at the moment.',
  },
};

/**
 * Faqja e hyrjes ne demo. Adresa u jepet shkollave dhe kompanive te interesuara.
 * Cdo karte hyn ne nje rol te ndryshem te te njejtes organizate demo.
 */
export default {
  name: 'DemoPage',

  data() {
    return {
      loading: null,
      error: '',
      lang: localStorage.getItem('locale') === 'en' ? 'en' : 'sq',
      views: [
        {
          key: 'organisation',
          title: { sq: 'Kompania e transportit', en: 'Transport company' },
          description: {
            sq: 'Menaxhon shkollat, autobusët, shoferët dhe raportet.',
            en: 'Manages schools, buses, drivers and reports.',
          },
          icon: 'mdi-domain',
          color: 'primary',
        },
        {
          key: 'school',
          title: { sq: 'Shkolla', en: 'School' },
          description: {
            sq: 'Sheh nxënësit, klasat dhe autobusët që i shërbejnë.',
            en: 'Sees its pupils, classes and the buses serving them.',
          },
          icon: 'mdi-school',
          color: 'teal',
        },
        {
          key: 'parent',
          title: { sq: 'Prindi', en: 'Parent' },
          description: {
            sq: 'Ndjek fëmijën dhe autobusin me të cilin udhëton.',
            en: 'Follows their child and the bus they travel on.',
          },
          icon: 'mdi-account-child',
          color: 'deep-orange',
        },
      ],
    };
  },

  computed: {
    txt() {
      return TEKSTET[this.lang];
    },
  },

  mounted() {
    // Nese localStorage eshte bosh, kjo faqe e vendos gjuhen fillestare —
    // perndryshe aplikacioni brenda do te hapej ne shqip pa e zgjedhur kush.
    this.setLang(this.lang);

    // ?lang=en lejon nje lidhje te gatshme per vizitore nderkombetare.
    const nga = this.$route.query.lang;
    if (nga === 'en' || nga === 'sq') this.setLang(nga);

    // Lidhja e drejtperdrejte /demo/prindi hyn menjehere, pa zgjedhje.
    const direct = this.$route.params.view;
    if (direct && this.views.some(v => v.key === direct)) {
      this.enter(direct);
    }
  },

  methods: {
    /**
     * Ruan zgjedhjen aty ku e lexon edhe pjesa e brendshme e aplikacionit
     * (AppLocale), qe gjuha te mos rikthehet ne shqip sapo te hyhet.
     */
    setLang(locale) {
      this.lang = locale;
      localStorage.setItem('locale', locale);
      loadLocaleMessagesAsync(locale).then(() => {
        setDocumentLang(locale);
        if (this.$vuetify && this.$vuetify.lang) {
          this.$vuetify.lang.current = locale;
        }
      });
    },

    async enter(view) {
      if (this.loading) return;

      this.loading = view;
      this.error = '';

      try {
        const user = await AuthService.demoLogin(view);
        if (!user) throw new Error(this.txt.failed);

        // Cdo rol ka panelin e vet.
        if (user.role_id === 2) this.$router.push('/school-dashboard');
        else if (user.role_id === 7) this.$router.push('/school-dashboard');
        else if (user.role_id === 4) this.$router.push('/parent-dashboard');
        else this.$router.push('/login');
      } catch (e) {
        this.error =
          e?.response?.data?.errors?.demo?.[0] ??
          e?.message ??
          this.txt.unavailable;
        this.loading = null;
      }
    },
  },
};
</script>

<style scoped>
.demo-page {
  max-width: 1000px;
  padding-top: 48px;
  padding-bottom: 48px;
}

.demo-card {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.demo-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12) !important;
}

.lang-switch {
  display: inline-flex;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.lang-opt {
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.55);
  background: transparent;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.lang-opt:hover {
  background: rgba(0, 0, 0, 0.04);
}

.lang-opt--active {
  background: #e8833a;
  color: #fff;
}

.lang-opt:focus-visible {
  outline: 2px solid #e8833a;
  outline-offset: -2px;
}
</style>
